import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Layers, Moon, Sun, LogOut, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import AuthDialog from './AuthDialog';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();
  const { darkMode, setDarkMode } = useTheme();
  const { user, logout, isAdmin } = useAuth();

  const navLinks = [
    { path: "/", label: "Inicio" },
    { path: "/wizard", label: "Asistente" },
    { path: "/quick", label: "Búsqueda Rápida" },
    { path: "/catalog", label: "Catálogo" },
    { path: "/compare", label: "Comparador" },
    { path: "/calculator", label: "Calculadora" },
  ];

  if (user) {
    navLinks.push({ path: "/favorites", label: "Favoritos" });
    if (isAdmin) {
      navLinks.push({ path: "/admin", label: "Admin" });
    }
  }

  return (
    <>
      <nav className="nav-industrial fixed top-0 left-0 right-0 z-50 bg-background/80" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Layers className="w-6 h-6 text-[#1a8ccc]" />
              <span className="font-bold text-xl tracking-tight">PrintMaterial Pro</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest font-medium transition-colors hover:text-[#1a8ccc] ${
                    location.pathname === link.path ? "text-[#1a8ccc]" : "text-muted-foreground"
                  }`}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDarkMode(!darkMode)}
                data-testid="theme-toggle"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {user ? (
                <div className="hidden lg:flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Hola, {user.name}</span>
                  <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAuthOpen(true)}
                  className="hidden lg:flex"
                  data-testid="login-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Acceder
                </Button>
              )}

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2"
                onClick={() => setIsOpen(!isOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {isOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-4" data-testid="mobile-menu">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-sm uppercase tracking-widest font-medium py-2"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <Button variant="outline" className="w-full" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </Button>
              ) : (
                <Button variant="outline" className="w-full" onClick={() => { setAuthOpen(true); setIsOpen(false); }}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Acceder
                </Button>
              )}
            </div>
          )}
        </div>
      </nav>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
};

export default Navigation;
